import Card from "components/card";
import Statistics from "./NursingStatistics";
import BedIcon from "components/icons/BedIcon";
import Transaction from "components/dataDisplay/Transaction";
import { useDealData } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";
import { useViewMode } from "contexts/ViewModeContext";

const Occupancy = () => {
    const { viewMode } = useViewMode();

    // Deal data
    const { currentFacility: dealFacility, selectedPeriod: dealSelectedPeriod } = useDealData();

    // Portfolio data
    const { currentPortfolioFacility, selectedPeriod: portfolioSelectedPeriod } = usePortfolioData();

    // Use appropriate data based on view mode
    const currentFacility = viewMode === 'portfolio' ? currentPortfolioFacility : dealFacility;
    const selectedPeriod = viewMode === 'portfolio' ? portfolioSelectedPeriod : dealSelectedPeriod;

    const details = currentFacility?.details || {};
    const periodData = currentFacility?.periods?.[selectedPeriod]?.Categories || {};
    const occupancyData = periodData?.Occupancy || [];

    // Get occupancy percentages - handle portfolio vs deal data differently
    let total, skilledCensus;

    if (viewMode === 'portfolio') {
        // For portfolio data, get occupancy rate directly from details
        total = details["Occupancy Rate"] || 0;
        skilledCensus = occupancyData?.find(item => item.Type === "Skilled")?.Percentages || 0;
    } else {
        // For deal data, use the existing structure
        total = occupancyData?.find(item => item.Type === "Occupancy")?.Percentages || 0;
        skilledCensus = occupancyData?.find(item => item.Type === "Skilled")?.Percentages || 0;
    }

    const roundedPercentage = total ? `${Math.round(parseFloat(total))}%` : "";
    const formattedSkilledCensus = skilledCensus !== 0 ? `${Math.round(parseFloat(skilledCensus))}%` : "";

    // For AL facilities (co-located with an SNF), show only AL/IL/MC beds
    const isAL = details.isALDuplicate;
    const bedTypes = isAL
        ? {
            "ALF Beds": details["ALF Beds"],
            "ILF Beds": details["ILF Beds"],
            "MC Beds": details["MC Beds"],
            "Total Beds":
                (parseFloat(details["ALF Beds"]) || 0) +
                (parseFloat(details["ILF Beds"]) || 0) +
                (parseFloat(details["MC Beds"]) || 0) || details["Total Beds"],
        }
        : {
            "SNF Beds": details["SNF Beds"],
            "ALF Beds": details["ALF Beds"],
            "ILF Beds": details["ILF Beds"],
            "MC Beds": details["MC Beds"],
            "Total Beds": details["Total Beds"],
        };

    return (
        <Card extra={"justify-around w-full p-[16px] bg-cover gap-2 flex flex-col"}>
            <h3 className="ml-6 mb-2 text-xl self-start font-bold text-gray-700 dark:text-white">
                Occupancy
            </h3>
            <Statistics
                subtitle1="Total"
                value1={roundedPercentage}
                subtitle2={skilledCensus !== 0 ? "Skilled Census" : ""}
                value2={formattedSkilledCensus}
                icon={
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-4xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                        <BedIcon />
                    </div>
                }
            />
            <div className="px-3">
                {Object.entries(bedTypes).map(([type, count]) => {
                    if (!count || count <= 0) return null;

                    const label = type === "ILF Beds" ? "IL Beds" : type;
                    const icon = type.split(" ")[0];

                    return (
                        <Transaction
                            key={type}
                            date={label}
                            sum={count}
                            icon={icon}
                        />
                    );
                })}
            </div>
        </Card>
    );
};

export default Occupancy;